import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import uniq from 'lodash/uniq';
import { DeviceDetectorService } from 'ngx-device-detector';
import { BehaviorSubject, Subject, throwError } from 'rxjs';
import { catchError, map, takeUntil, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { environment } from '../../../environments';
import { PrismHeaders, UserTypeEnum } from '../../enums';
import { Response, LinkModel } from '../../models';
import {
	AgentService,
	AssetsService,
	DialogService,
	ProductsService,
	SnackbarService,
} from '../../services';
import { BRANDS } from '../../../luxengine-brand-kit/brands';
import { DownloadSelectionModalComponent } from '../download-selection-modal/download-selection-modal.component';
import { NotifyAgentDialogComponent } from '../notify-agent-dialog/notify-agent-dialog.component';

@Component({
	selector: 'prism-agent-job-view',
	templateUrl: './agent-job-view.component.html',
	styleUrls: ['./agent-job-view.component.scss'],
})
export class AgentJobViewComponent implements OnInit, OnDestroy {
	isSelecting = false;
	showLoader = true;
	mobileData: { isMobile: boolean; browser: string; os: string } = null;
	shareUid: string;
	downloadOptions = {
		web: false,
		watermark: false,
	};
	urls = new BehaviorSubject(null);
	jobProductsData$: Observable<Response>;
	subDestroyer$ = new Subject<void>();
	assetsSelected = [];
	servicesLoading = [];
	assetPaths$ = this.assetsService.currentJobAssetsPaths$.pipe(
		tap((paths) => {
			this.downloadOptions.web = paths.some((path) => path.includes('web'));
			this.downloadOptions.watermark = paths.some((path) =>
				path.includes('watermark'),
			);
		}),
	);
	links: LinkModel[] = [];
	bwrmLogo = '../../../assets/images/bwrm-new-logo.png';
	luxEngineLogo = '../../../assets/lux-engine/lux-engine-logo.png';
	logoSrc: string = null;
	logoAlt = '';
	brandSiteUrl = 'https://bwrm.com.au/';
	constructor(
		private productsService: ProductsService,
		private agentService: AgentService,
		private http: HttpClient,
		private activatedRoute: ActivatedRoute,
		private dialogService: DialogService,
		private assetsService: AssetsService,
		private snackbarService: SnackbarService,
		private deviceDetector: DeviceDetectorService,
	) {
		this.shareUid = this.activatedRoute.snapshot.params.shareUid || '';
	}

	ngOnInit() {
		// Hide Intercom chat for agent job view
		if (window.Intercom) {
			window.Intercom('hide');
		}

		const os = this.deviceDetector.os;

		this.mobileData = {
			isMobile: os === 'Android' || os === 'iOS',
			browser: this.deviceDetector.browser,
			os,
		};
		this.jobProductsData$ = this.http
			.get(`${environment.apiBaseUrl}/member-get-agent-job-products`, {
				headers: new HttpHeaders().append(
					PrismHeaders.QueryData,
					JSON.stringify({ shareUid: this.shareUid }),
				),
			})
			.pipe(
				tap((jobData: Response) => {
					const services = jobData.data.products.map(
						(product) => product.services,
					);

					this.applyBranding(jobData.data);
					this.assetsService.getAgencyAssetsBucketPaths(
						this.shareUid,
						services,
					);
				}),
				takeUntil(this.subDestroyer$),
				map((d: Response) => {
					this.loadLinks();
					return d.data;
				}),
			);
	}

	ngOnDestroy() {
		this.subDestroyer$.next();

		// Show Intercom chat when leaving agent job view
		if (window.Intercom) {
			window.Intercom('show');
		}
	}

	openDownloadAllOptions(jobData) {
		this.dialogService.openDialog(
			DownloadSelectionModalComponent,
			{
				shareUid: this.shareUid,
				address: jobData.address,
				jobId: jobData.jobId,
				allAssetPaths: this.assetsService.currentJobAssetPaths,
			},
			{
				panelClass: 'download-selection-dialog',
			},
		);
	}

	changeLocation() {
		window.open(this.brandSiteUrl);
	}

	private applyBranding({
		userType,
		companyName,
		companyLogoUrl,
	}: {
		userType?: string;
		companyName?: string;
		companyLogoUrl?: string;
	}): void {
		const isLuxEngine = userType === UserTypeEnum.LUX_ENGINE;

		this.logoSrc =
			companyLogoUrl || (isLuxEngine ? this.luxEngineLogo : this.bwrmLogo);
		this.logoAlt =
			companyName ||
			(isLuxEngine ? BRANDS.luxengine.displayName : BRANDS.bwrm.displayName);
		this.brandSiteUrl = isLuxEngine
			? 'https://luxengine.com/'
			: 'https://bwrm.com.au/';
	}

	openShareModal(job) {
		this.dialogService.openDialog(
			NotifyAgentDialogComponent,
			{ job, asAgency: true, mobileData: this.mobileData },
			{
				panelClass: 'mobile-dialog-container',
			},
		);
	}

	toggleSelect() {
		this.isSelecting = !this.isSelecting;
		this.assetsSelected = [];
	}

	updateAssetsSelected({ assetData, selected }) {
		if (selected) {
			this.assetsSelected.push(assetData);
		} else {
			this.assetsSelected = this.assetsSelected.filter((asset) => {
				return asset.originalFilePath !== assetData.originalFilePath;
			});
		}
	}

	updateLoader(servicesLoadIdData, loaded: boolean) {
		if (loaded) {
			this.servicesLoading = this.servicesLoading.filter(
				(id) => id !== servicesLoadIdData,
			);
		} else {
			this.servicesLoading = uniq([
				...this.servicesLoading,
				...servicesLoadIdData,
			]);
		}
		this.showLoader = !!this.servicesLoading.length;
	}

	downloadSelected(jobData) {
		let downloadPaths = [];
		const allJobAssetPaths = this.assetsService.currentJobAssetPaths;
		this.assetsSelected.forEach((asset) => {
			const currentAssetPaths = allJobAssetPaths.filter((path) => {
				return path.includes(asset.originalFileName);
			});

			downloadPaths = downloadPaths.concat(currentAssetPaths);
		});
		this.dialogService.openDialog(
			DownloadSelectionModalComponent,
			{
				shareUid: this.shareUid,
				address: jobData.address,
				jobId: jobData.jobId,
				selectedAssets: downloadPaths,
			},
			{
				panelClass: 'download-selection-dialog',
			},
		);
	}

	private loadLinks() {
		this.http
			.get<{ data: { links: LinkModel[]; address: string; jobId: number } }>(
				`${environment.apiBaseUrl}/member-get-agent-links`,
				{
					headers: new HttpHeaders().append(
						PrismHeaders.QueryData,
						JSON.stringify({ shareUid: this.shareUid }),
					),
				},
			)
			.pipe(
				map((res) => res.data),
				catchError((error) => {
					console.error('Error fetching member links:', error);
					this.snackbarService.showSnackbar('Failed to fetch links');
					return throwError(error);
				}),
			)
			.subscribe((res) => {
				this.links = res.links;
			});
	}
}
